<template>
    <div
        v-if="showResults"
        class="fulltext-search-results bg-white border border-gray-200 rounded-lg shadow-none shadow-md z-50 flex flex-col"
        :class="containerClass"
        data-test="fulltext-search-results"
    >
        <div class="flex-1 overflow-y-auto max-h-[70vh]">
            <NuxtLink
                v-for="(result, index) in results"
                :key="result.id"
                :class="['block fulltext-search-result-item hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 p-4', { 'focused': index === selectedIndex }]"
                tabindex="0"
                data-test="fulltext-search-result-item"
                :to="resultNavigationPath(result)"
                rel="noopener noreferrer"
                @click="selectResult(result)"
                @keyup.enter="selectResult(result)"
            >
                <div
                    class="font-medium text-gray-900"
                    :class="titleClass"
                    data-test="fulltext-search-result-title"
                    v-html="result._formatted.title"
                />
                <div
                    class="text-gray-600 mt-1"
                    :class="excerptClass"
                    data-test="fulltext-search-result-excerpt"
                    v-html="result._formatted.content"
                />
            </NuxtLink>

            <FulltextSearchNoResults
                v-if="!results.length"
                data-test="fulltext-search-no-results"
            />
        </div>
        
        <FulltextSearchViewAllLink
            :view-all-url="viewAllUrl"
            :view-all-text="viewAllText"
            @view-all-clicked="emit('view-all-clicked')"
        />
    </div>
</template>

<script setup lang="ts">
    import type { SearchResult } from '~/types/fulltextSearch';
    import FulltextSearchViewAllLink from './FulltextSearchViewAllLink.vue';
    import FulltextSearchNoResults from './FulltextSearchNoResults.vue';
    import { resultNavigationPath } from '~/utils/searchResultNavigation.ts';

    type FulltextSearchResultsProps = {
        results: SearchResult[];
        showResults: boolean;
        viewAllUrl: string;
        viewAllText: string;
        containerClass?: string;
        titleClass?: string;
        excerptClass?: string;
        selectedIndex?: number;
    };

    type FulltextSearchResultsEmits = {
        'result-selected': [result: SearchResult];
        'view-all-clicked': [];
    };
    
    withDefaults(defineProps<FulltextSearchResultsProps>(), {
        containerClass: 'mt-4 max-h-80',
        titleClass: 'text-sm',
        excerptClass: 'text-xs',
        selectedIndex: -1
    });

    const emit = defineEmits<FulltextSearchResultsEmits>();

    const selectResult = (result: SearchResult) => {
        emit('result-selected', result);
    };
</script>

<style scoped lang="scss">
    .fulltext-search-result-item {
        transition: all 0.15s ease;
        overflow-wrap: anywhere;
        
        &:hover, &.focused {
            background-color: rgba(0, 0, 0, 0.02);
            outline: none;
        }

        &.focused {
            box-shadow: inset 2px 0 0 var(--button-color, #3b82f6);
        }

        a {
            text-decoration: none;
            color: inherit;
            
            &:hover {
                text-decoration: none;
            }
        }
    }
</style>
